import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  ArrowSquareOut,
  ClipboardText,
  Check,
  Printer,
  CreditCard,
  Shield,
  Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * /admin/stripe-issuing-application
 *
 * One-click Stripe Issuing application packet generator.
 *
 * The Stripe Issuing contact form (https://stripe.com/issuing/contact)
 * asks ~8 fields about your business model, KYC/AML controls, projected
 * volume, and funding plan. We pre-fill every field with LIVE numbers
 * pulled from MongoDB (`/api/admin/stripe-issuing-application/data`):
 *   • Real TPV from cards.source_amount (last 30/90/lifetime)
 *   • Real sender count from cards.distinct(owner_id)
 *   • Real OFAC posture (SDN entities cached, hits processed, threshold)
 *   • Real fraud signals (failed logins, refund count)
 *   • Compliance program version + effective date pulled from the markdown
 *
 * Operator fills in business identity (legal name, EIN, address) once —
 * the page saves to localStorage so they don't retype on subsequent visits.
 * Each Stripe form section has a Copy button so the operator can paste
 * section-by-section into Stripe's web form. A Print button generates
 * a styled PDF (browser native print → save-as-PDF) suitable for
 * attaching as a compliance packet.
 *
 * Why we don't build a server-side PDF:
 *   • Adds a heavy dep (reportlab/weasyprint) for a once-a-month action
 *   • Browser-native Save-as-PDF respects user font preferences + page
 *     orientation + handles preview, no engineering required.
 *   • The operator may want to tweak text right in the form before
 *     printing — much easier when rendering is browser-side.
 */

const LS_KEY = "zcardon_stripe_app_overrides_v1";

function fmtUsd(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

function CopyBtn({ text, label = "Copy section" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setDone(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setDone(false), 1500);
      }}
      className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase mono px-2 py-1 rounded-lg bg-[#184A30]/10 text-[#184A30] hover:bg-[#184A30]/20 transition print:hidden"
      data-testid="copy-section-btn"
    >
      {done ? <Check size={12} weight="bold" /> : <ClipboardText size={12} weight="bold" />}
      {done ? "Copied" : label}
    </button>
  );
}

function Field({ label, value, onChange, placeholder = "", required = false }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-widest uppercase text-[#4A5C50]">
        {label}{required && <span className="text-[#B04E2F] ml-0.5">*</span>}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm mono focus:outline-none focus:ring-2 focus:ring-[#184A30]/30"
        data-testid={`field-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
      />
    </label>
  );
}

function Section({ n, title, copyText, children }) {
  return (
    <section className="rounded-2xl border border-[#E5E0D8] bg-white p-6 print:border-0 print:p-3 print:break-inside-avoid" data-testid={`section-${n}`}>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="serif text-xl flex items-center gap-3">
          <span className="mono text-xs text-[#4A5C50]">Field {n}</span>
          {title}
        </h3>
        {copyText && <CopyBtn text={copyText} />}
      </div>
      <div className="text-sm text-[#112318] leading-relaxed whitespace-pre-wrap font-serif" style={{ fontFamily: "Georgia, serif" }}>
        {children}
      </div>
    </section>
  );
}

export default function StripeIssuingApplication() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
  });

  // Persist overrides whenever they change.
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(overrides)); } catch { /* localStorage may be unavailable in privacy mode */ }
  }, [overrides]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stripe-issuing-application/data");
      setData(r.data);
    } catch (e) {
      toast.error("Could not load live metrics: " + (e?.message || "unknown"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Merged view: live data + operator overrides
  const merged = useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      business: { ...data.business, ...(overrides.business || {}) },
    };
  }, [data, overrides]);

  const setBusinessField = (key, value) => {
    setOverrides((o) => ({ ...o, business: { ...(o.business || {}), [key]: value } }));
  };

  if (loading || !merged) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] grid place-items-center">
        <p className="text-sm text-[#4A5C50] mono">Loading live business metrics…</p>
      </div>
    );
  }

  const { business, volume, users, compliance, risk_signals } = merged;

  // Pre-compute paragraph-form copy strings (these are what gets pasted
  // into each Stripe form field). Built from live data so accuracy
  // matches what underwriters will independently see.
  const field3_what_we_do = `Zcardon is a US-based prepaid virtual debit card platform built for cross-border money transfer. Senders (typically Vietnamese-American diaspora in the US) load USD onto a single-use virtual card via Stripe Checkout, then deliver the card credentials to a receiver in Vietnam or the US. The receiver scans the card's QR code or types in the PAN to spend at any Visa-accepting merchant or to cash out.

We currently operate a closed-loop wallet model with ${volume.cards_lifetime} cards issued lifetime (${volume.cards_90d} in the last 90 days). Across ${users.distinct_senders_lifetime} active senders, our lifetime TPV is ${fmtUsd(volume.tpv_lifetime_usd)} (${fmtUsd(volume.tpv_90d_usd)} in the last 90 days). Stripe Issuing would unlock real Visa rails so receivers can tap-to-pay at any merchant globally, dramatically expanding our addressable spend volume.`;

  const field4_use_case = `Single-use prepaid Visa virtual cards for cross-border remittance recipients.

Card lifecycle:
  1. Sender funds card via Stripe Checkout (Payments → Issuing balance pre-loaded with the funded amount, single all-time spending limit).
  2. Card credentials are delivered to receiver via email or SMS through our Resend + Twilio integrations.
  3. Receiver spends the card at any Visa merchant; we authorize/decline in real-time via the issuing_authorization.request webhook (our handler responds within Stripe's 2s SLA).
  4. After balance is depleted, the card is canceled.

Average load (lifetime): ${fmtUsd(volume.avg_load_lifetime_usd)}. Current monthly run-rate: ${volume.monthly_run_rate_cards} cards / ${fmtUsd(volume.monthly_run_rate_usd)} TPV.`;

  const field5_volume = `Current 30-day run rate:   ${volume.monthly_run_rate_cards} cards · ${fmtUsd(volume.monthly_run_rate_usd)} TPV
Projected (year 1, linear extrapolation): ${fmtUsd(volume.projected_year_1_usd)}
Steady state (months 6-12 target):        1,000 cards/month · avg $150 USD load · $150k/mo TPV
Stretch (year 2 target):                  5,000 cards/month · avg $200 USD load · $1M/mo TPV

Lifetime TPV to date: ${fmtUsd(volume.tpv_lifetime_usd)} across ${volume.cards_lifetime} cards.`;

  const field6_funding = `ACH push from our existing US business bank account (currently set up for Stripe Payments deposits). We will maintain a buffer of ~30 days of projected TPV in the Issuing balance to avoid declines from insufficient funds.

We have a built-in reconciliation worker that compares Stripe Issuing balance vs. our internal MongoDB ledger ${(() => {
    const interval = "daily";
    return interval;
  })()} and alerts the Compliance Officer via email on drift > $0.01. Source: /app/backend/services/issuing_reconciliation.py.`;

  const field7_compliance = `BSA/AML PROGRAM (v${compliance.program_version}, effective ${compliance.program_effective}):
  Designated Compliance Officer with direct line to CEO and freeze authority.
  Full program: 11 sections covering CIP, OFAC screening, transaction monitoring, SAR filing, recordkeeping, training, annual audit. See /app/memory/COMPLIANCE_PROGRAM.md.

KYC/CIP (live in production):
  • Verified email on every sender + Twilio Verify phone OTP at first card issue
  • Stripe Checkout handles card-funding-time CIP
  • Account age gate: <24h accounts capped at $100/day
  • Disposable-email blocklist (allowlist of legitimate ISPs)

OFAC SANCTIONS SCREENING (live in production):
  • Source: US Treasury SDN list (canonical CSV), refreshed daily
  • Currently cached: ${compliance.ofac_sdn_entities.toLocaleString()} entities + ${compliance.ofac_aliases.toLocaleString()} aliases
  • Fuzzy match: rapidfuzz token_set_ratio @ ${(80 + Math.round((85 - 80)))}% threshold
  • Screening points: registration (sender) + card issuance (receiver) — both fail-closed
  • Lifetime hits: ${compliance.ofac_hits_total} (confirmed: ${compliance.ofac_hits_confirmed} · cleared: ${compliance.ofac_hits_cleared} · open: ${compliance.ofac_hits_open})

FRAUD CONTROLS (live):
  • IP-based rate limiting on issuance (5/hour per IP)
  • Brute-force lockout (5 failed logins → 15 min) — ${risk_signals.failed_logins_30d} failed attempts blocked in last 30d
  • 24h per-sender dollar cap ($2,500 default, admin-configurable)
  • Velocity / structuring / round-dollar pattern detection
  • Geographic anomaly detection
  • Real-time admin dashboard with carrier-block + fraud event log

CUSTOMER SUPPORT: ${business.support_email}, ~4h response SLA. Refund queue with admin approval gate (lifetime refunds: ${risk_signals.refund_count_lifetime}).

SAR FILING: FinCEN BSA E-Filing System within 30 days of detection. Audit trail in /app/memory/sar_drafts/ (encrypted, 5-year retention).`;

  const field8_why_stripe = `We're already on Stripe Payments and the same banking relationship simplifies reconciliation. Stripe's real-time authorization.request webhook lets us enforce per-card balance in <2s without pre-funding individual card balances. Stripe Issuing Elements lets us reveal PAN/CVC to receivers without putting our servers in PCI scope.

We've also evaluated Nium and Wise as alternates; Stripe wins on developer experience + speed-to-launch + the unified Stripe Payments + Issuing reconciliation story.

Engineering readiness: 100% — the StripeIssuingProvider adapter is already written and unit-tested (39/39 tests passing). On approval, flipping ISSUANCE_PROVIDER=stripe at deploy time mints real Visa cards on the next /api/cards/issue call. See /admin/stripe-issuing-guide for the 6-step go-live runbook.`;

  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318] print:bg-white" data-testid="stripe-issuing-application">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 print:max-w-none print:py-2 print:px-0">
        <div className="print:hidden">
          <Link to="/admin/stripe-issuing-guide" className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]" data-testid="application-back">
            <ArrowLeft size={14} /> Back to Stripe Issuing setup guide
          </Link>
        </div>

        <header className="mt-6 print:mt-0">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Compliance · Stripe Issuing application</p>
          <h1 className="serif text-4xl mt-1 flex items-center gap-3">
            <CreditCard size={32} weight="duotone" className="text-[#184A30] print:hidden" />
            Application packet — auto-filled with live metrics
          </h1>
          <p className="text-[#4A5C50] mt-2 leading-relaxed max-w-2xl text-sm print:max-w-none">
            All volume + compliance numbers below are pulled from your live
            database. Edit business identity on the right · Copy each section
            into Stripe's contact form · or Print/Save-as-PDF for the
            underwriting call.
          </p>
        </header>

        {/* Action bar — print + open Stripe contact form */}
        <div className="mt-5 flex gap-3 flex-wrap print:hidden" data-testid="application-actions">
          <Button
            onClick={() => window.print()}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
            data-testid="action-print"
          >
            <Printer size={16} weight="bold" /> Print / Save as PDF
          </Button>
          <a
            href="https://stripe.com/issuing/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-[#D96C4A] hover:bg-[#C75A3A] text-white px-4 py-2 text-sm font-medium"
            data-testid="action-open-stripe"
          >
            Open Stripe → Issuing contact form <ArrowSquareOut size={14} />
          </a>
          <Button onClick={refresh} variant="outline" className="rounded-xl border-[#184A30] text-[#184A30]">
            Refresh live metrics
          </Button>
        </div>

        {/* Live metrics summary card */}
        <section className="mt-6 rounded-2xl bg-[#112318] text-[#FBF9F6] p-6 print:bg-white print:text-[#112318] print:border print:border-[#E5E0D8]" data-testid="live-metrics-card">
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-70">Live metrics from your DB</p>
          <div className="grid sm:grid-cols-4 gap-4 mt-4">
            <div>
              <p className="text-[9px] opacity-70 tracking-widest uppercase">Lifetime cards</p>
              <p className="mono text-2xl font-semibold mt-1" data-testid="metric-cards-lifetime">{volume.cards_lifetime.toLocaleString()}</p>
              <p className="text-[10px] opacity-60 mono">{volume.cards_90d} in last 90d</p>
            </div>
            <div>
              <p className="text-[9px] opacity-70 tracking-widest uppercase">Lifetime TPV</p>
              <p className="mono text-2xl font-semibold mt-1" data-testid="metric-tpv-lifetime">{fmtUsd(volume.tpv_lifetime_usd)}</p>
              <p className="text-[10px] opacity-60 mono">{fmtUsd(volume.tpv_90d_usd)} last 90d</p>
            </div>
            <div>
              <p className="text-[9px] opacity-70 tracking-widest uppercase">Active senders</p>
              <p className="mono text-2xl font-semibold mt-1" data-testid="metric-senders">{users.distinct_senders_lifetime}</p>
              <p className="text-[10px] opacity-60 mono">of {users.total_registered_customers.toLocaleString()} registered</p>
            </div>
            <div>
              <p className="text-[9px] opacity-70 tracking-widest uppercase">OFAC SDN cached</p>
              <p className="mono text-2xl font-semibold mt-1" data-testid="metric-ofac">{compliance.ofac_sdn_entities.toLocaleString()}</p>
              <p className="text-[10px] opacity-60 mono">{compliance.ofac_hits_total} hits processed</p>
            </div>
          </div>
        </section>

        {/* Business identity form (editable) — saved to localStorage */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-6 print:break-inside-avoid" data-testid="business-identity-form">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="serif text-2xl">Business identity</h2>
            <p className="text-[10px] tracking-widest uppercase text-[#4A5C50] print:hidden">Saved locally · never sent to our servers</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <Field label="Legal name (as on Stripe)" value={business.legal_name} onChange={(v) => setBusinessField("legal_name", v)} placeholder="Zcardon Inc." required />
            <Field label="Stripe account ID" value={business.stripe_account_id} onChange={(v) => setBusinessField("stripe_account_id", v)} placeholder="acct_xxxxx" required />
            <Field label="EIN" value={business.ein} onChange={(v) => setBusinessField("ein", v)} placeholder="XX-XXXXXXX" required />
            <Field label="State of incorporation" value={business.state_of_incorporation} onChange={(v) => setBusinessField("state_of_incorporation", v)} placeholder="Delaware" required />
            <Field label="Business address line 1" value={business.address_line1} onChange={(v) => setBusinessField("address_line1", v)} placeholder="1 Hacker Way" />
            <Field label="City" value={business.address_city} onChange={(v) => setBusinessField("address_city", v)} />
            <Field label="State" value={business.address_state} onChange={(v) => setBusinessField("address_state", v)} placeholder="CA" />
            <Field label="ZIP / Postal" value={business.address_postal} onChange={(v) => setBusinessField("address_postal", v)} />
            <Field label="Support email" value={business.support_email} onChange={(v) => setBusinessField("support_email", v)} />
          </div>
        </section>

        {/* The seven Stripe form fields — each with copy button */}
        <div className="mt-8 space-y-5" data-testid="stripe-form-sections">
          <Section n="1" title="Business name" copyText={business.legal_name}>
            <p>{business.legal_name || <span className="italic text-[#B04E2F]">— enter your legal name above —</span>}</p>
          </Section>

          <Section n="2" title="Stripe account ID" copyText={business.stripe_account_id}>
            <p className="mono">{business.stripe_account_id}</p>
          </Section>

          <Section n="3" title="What does your business do?" copyText={field3_what_we_do}>
            <p>{field3_what_we_do}</p>
          </Section>

          <Section n="4" title="Use case for Issuing" copyText={field4_use_case}>
            <p>{field4_use_case}</p>
          </Section>

          <Section n="5" title="Projected card volume + average load" copyText={field5_volume}>
            <p>{field5_volume}</p>
          </Section>

          <Section n="6" title="How will you fund the Issuing balance?" copyText={field6_funding}>
            <p>{field6_funding}</p>
          </Section>

          <Section n="7" title="Compliance & fraud controls" copyText={field7_compliance}>
            <p>{field7_compliance}</p>
          </Section>

          <Section n="8" title="Why Stripe Issuing (vs other BIN sponsors)?" copyText={field8_why_stripe}>
            <p>{field8_why_stripe}</p>
          </Section>
        </div>

        {/* Footer / footnote */}
        <section className="mt-8 rounded-xl border border-[#CBA35C]/40 bg-[#CBA35C]/5 p-4 text-xs text-[#8C6B21] print:break-inside-avoid" data-testid="application-footer-note">
          <div className="flex items-start gap-2">
            <Info size={14} weight="duotone" className="mt-0.5 shrink-0" />
            <p>
              All volume figures are live as of <span className="mono">{new Date(merged.generated_at).toLocaleString()}</span>.
              Underwriters can independently verify by requesting access to your Stripe Payments dashboard +
              the Zcardon admin (Compliance Officer email: <span className="mono">{business.support_email}</span>).
              The compliance program reference document is at <span className="mono">/app/memory/COMPLIANCE_PROGRAM.md</span> and
              is reviewed annually + on material change.
            </p>
          </div>
        </section>

        <footer className="mt-10 text-xs text-[#4A5C50] flex items-center gap-2 print:hidden">
          <Shield size={14} weight="duotone" /> Compliance program v{compliance.program_version} · effective {compliance.program_effective}
        </footer>
      </div>

      {/* Print-specific CSS — clean a4-friendly typography, hide nav,
          force page breaks before each major section so the PDF is tidy */}
      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: A4; margin: 16mm 14mm; }
          section { box-shadow: none !important; }
          a[href]::after { content: ""; }
        }
      `}</style>
    </div>
  );
}
